import {
  Badge,
  Banner,
  Box,
  Button,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { produce } from 'immer';
import { ampli } from 'lib-shared-tracking';
import { FunctionComponent, useCallback, useState } from 'react';
import { Field, Form, InjectedFormProps } from 'redux-form';
import { v4 as uuid } from 'uuid';

import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { TimezoneSelector } from 'components/timezoneSelector';
import type { FlowManagerEditModalComponentT } from 'fable/flowEditor';
import { articles } from 'utils/elevio';

import type { StateProps } from '../../containers/modals/openingHours';
import type {
  FlowNodeOpeningHoursT,
  OpeningHoursNodeIntervalsT,
  OpeningHoursNodeScheduleT,
} from '../nodeTypes';
import type { OpeningHoursNode } from '../nodeTypes/canvas/openingHours';
import { FlowNodeTypes } from '../types';

import { MigrateOpeningHours } from './migrateOpeningHours/migrateOpeningHours';
import type { BaseProps as MigrateOpeningHoursProps } from './migrateOpeningHours/types/props';
import styles from './openingHours.module.scss';
import { Day } from './openinghours/day';

export type OwnProps = FlowManagerEditModalComponentT<FlowNodeOpeningHoursT>;

export type Props = OwnProps &
  StateProps &
  InjectedFormProps<OpeningHoursModalFormValues, Props>;

export interface OpeningHoursModalFormValues {
  timezone: string;
}

export function createOpeningHoursModal(
  isDeprecated: boolean,
): FunctionComponent<Props> {
  return function OpeningHours({
    dirty,
    initialValues,
    node,
    nodeLabel,
    handleSubmit,
    replaceNode,
    triggerClose,
  }: Props): JSX.Element {
    const { payload } = node;
    const { schedule: payloadSchedule } = payload || {};

    const [isMigrating, setIsMigrating] = useState(false);
    const [schedule, setSchedule] = useState<OpeningHoursNodeScheduleT[]>(
      initialValues.schedule,
    );
    const hasScheduleChanged = useIsChanged(
      [payloadSchedule, schedule],
      [payloadSchedule, schedule],
    );

    const onSaveClick = useCallback(
      (values: OpeningHoursModalFormValues) => {
        const { timezone } = values;

        const newNode: FlowNodeOpeningHoursT = {
          ...node,
          badges: [],
          childIds: [],
          icon: 'i-clock',
          isAction: false,
          isEditable: true,
          isRemovable: true,
          nodeType: FlowNodeTypes.OPENING_HOURS,
          payload: {
            closedNodeId: payload?.closedNodeId || uuid(),
            openNodeId: payload?.openNodeId || uuid(),
            schedule,
            timezone,
          },
          text: 'Opening hours',
        };

        triggerClose();
        replaceNode(newNode);
      },
      [node, payload, replaceNode, schedule, triggerClose],
    );

    const onDayChange = useCallback(
      (weekDay: number, intervals: OpeningHoursNodeIntervalsT[]) => {
        setSchedule(
          produce((schedule) => {
            schedule[weekDay].intervals = intervals;
          }),
        );
      },
      [setSchedule],
    );

    if (isMigrating) {
      return (
        <MigrateOpeningHours
          node={node as OpeningHoursNode}
          triggerClose={triggerClose}
          replaceNode={
            // Type-cast because we need to replace the node with a business hours node.
            replaceNode as unknown as MigrateOpeningHoursProps['replaceNode']
          }
        />
      );
    }

    return (
      <Box width="600px">
        <ModalHead
          title={
            <>
              {nodeLabel}
              {isDeprecated && (
                <Badge size="small" variant="info" className={styles.badge}>
                  Update available
                </Badge>
              )}
            </>
          }
          onClose={triggerClose}
        />
        <ModalBody>
          {isDeprecated && (
            <Box margin="0 0 400">
              <Banner
                icon="gift"
                variant="announcement"
                headline="Turn your opening hours into schedules"
                description={
                  <FlexBox gap="500">
                    <Box fontSize="12px">
                      Schedules make it easy to plan ahead, keep up with
                      holidays and manage your customers’ expectations.{' '}
                      <Text.ExternalLink
                        className={styles.externalLink}
                        href="#"
                        data-elevio-article={
                          articles.settings.businessHours.announcement
                        }
                        data-elevio-style="nothing"
                      >
                        Learn more
                      </Text.ExternalLink>
                    </Box>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        ampli.businessHoursMigrationWizardStartUpdateButtonClicked();
                        setIsMigrating(true);
                      }}
                    >
                      Start update
                    </Button>
                  </FlexBox>
                }
              />
            </Box>
          )}
          {!isDeprecated && (
            <Text.Paragraph className={styles.description}>
              By setting your opening hours, you can decide what happens to
              incoming conversations based on whether your business is open or
              closed.
            </Text.Paragraph>
          )}
          <div className={styles.form}>
            <Form onSubmit={handleSubmit(onSaveClick)}>
              <Field
                className={styles.timezone}
                component={TimezoneSelector}
                name="timezone"
                required
              />
              <div className={styles.days}>
                {schedule.map(
                  (day: OpeningHoursNodeScheduleT, index: number) => (
                    <Day
                      key={day.date}
                      index={index}
                      day={day}
                      onDayChange={onDayChange}
                    />
                  ),
                )}
              </div>
            </Form>
          </div>
        </ModalBody>
        <ModalFoot
          confirmOnClick={handleSubmit(onSaveClick)}
          confirmProps={{ disabled: !dirty && !hasScheduleChanged }}
          confirmName={getConfirmButtonVerb(node)}
          cancelOnClick={triggerClose}
        />
      </Box>
    );
  };
}
